import { BucketingStrategy, SWITCHBACK_MIN_SLOT_MS, SwitchbackScope } from "./types";
/**
 * Shape of a mad.Key: 21 bytes, unpadded URL-safe base64. That it is an org key
 * (any org type) is enforced server-side; checking it here would need the async
 * key-type digest.
 */
const MAD_KEY_PATTERN = /^[A-Za-z0-9_-]{28}$/;
export function isMadKeyShaped(value) {
    return typeof value === "string" && MAD_KEY_PATTERN.test(value);
}
export const validators = {
    name: (_experiment, value) => {
        if (typeof value !== "string" || value.trim().length === 0) {
            return "Experiment name is required";
        }
        return undefined;
    },
    advertiserId: (_experiment, value) => {
        if (value == null || value === "") {
            return undefined; // unset = internal/global experiment
        }
        if (!isMadKeyShaped(value)) {
            return "advertiserId must be a valid org key";
        }
        return undefined;
    },
    creatorEmail: (_experiment, value) => {
        if (!value || (typeof value === "string" && value.trim().length === 0)) {
            return "creatorEmail is required";
        }
        return undefined;
    },
    endDate: (experiment, value) => {
        if (value === null) {
            return undefined; // null = Until Cancelled, always valid
        }
        if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
            return "endDate must be a valid date";
        }
        if (experiment.startDate instanceof Date &&
            !Number.isNaN(experiment.startDate.getTime()) &&
            value <= experiment.startDate) {
            return "endDate must be after startDate";
        }
        return undefined;
    },
    participationRate: (_experiment, value) => {
        if (value != null && (typeof value !== "number" || value < 0 || value > 1)) {
            return "participationRate must be a number between 0 and 1";
        }
        return undefined;
    },
    exposureSampleRate: (_experiment, value) => {
        if (value != null && (typeof value !== "number" || value < 0 || value > 1)) {
            return "exposureSampleRate must be a number between 0 and 1";
        }
        return undefined;
    },
    maxImpressionCount: (_experiment, value) => validateOptionalCap("maxImpressionCount", value),
    maxExposureCount: (_experiment, value) => validateOptionalCap("maxExposureCount", value),
    switchbackConfig: (experiment, value) => {
        const isSwitchback = experiment.bucketingStrategy === BucketingStrategy.SWITCHBACK;
        if (value == null) {
            return isSwitchback
                ? "switchbackConfig is required when bucketingStrategy is SWITCHBACK"
                : undefined;
        }
        if (!isSwitchback) {
            return "switchbackConfig can only be set when bucketingStrategy is SWITCHBACK";
        }
        if (!Object.values(SwitchbackScope).includes(value.scope)) {
            return `switchbackConfig.scope ${value.scope} is not supported`;
        }
        if (!Number.isSafeInteger(value.slotMs) || value.slotMs < SWITCHBACK_MIN_SLOT_MS) {
            return `switchbackConfig.slotMs must be a whole number of at least ${SWITCHBACK_MIN_SLOT_MS}`;
        }
        if (!Number.isSafeInteger(value.gapMs) || value.gapMs < 0) {
            return "switchbackConfig.gapMs must be a whole number of at least 0";
        }
        if (value.burnInMs !== undefined &&
            (!Number.isSafeInteger(value.burnInMs) || value.burnInMs < 0)) {
            return "switchbackConfig.burnInMs must be a whole number of at least 0";
        }
        if (value.epoch !== undefined &&
            (!(value.epoch instanceof Date) || Number.isNaN(value.epoch.getTime()))) {
            return "switchbackConfig.epoch must be a valid date";
        }
        return undefined;
    },
    eligibilityCriteria: (_experiment, value) => {
        if (value == null) {
            return undefined;
        }
        if (value.minBudget > value.maxBudget) {
            return `minBudget (${value.minBudget}) must not exceed maxBudget (${value.maxBudget})`;
        }
        if (value.minDurationDays > value.maxDurationDays) {
            return `minDurationDays (${value.minDurationDays}) must not exceed maxDurationDays (${value.maxDurationDays})`;
        }
        if (value.minImpressionGoal != null &&
            value.maxImpressionGoal != null &&
            value.minImpressionGoal > value.maxImpressionGoal) {
            return `minImpressionGoal (${value.minImpressionGoal}) must not exceed maxImpressionGoal (${value.maxImpressionGoal})`;
        }
        return undefined;
    },
    includedEntities: (experiment, value) => {
        const included = value || [];
        const excluded = experiment.excludedEntities || [];
        const includedIds = included.map((e) => e.entityId);
        const duplicateIncluded = includedIds.filter((id, i) => includedIds.indexOf(id) !== i);
        if (duplicateIncluded.length > 0) {
            return `Duplicate entityId in includedEntities: ${[...new Set(duplicateIncluded)].join(", ")}`;
        }
        const excludedIds = excluded.map((e) => e.entityId);
        const duplicateExcluded = excludedIds.filter((id, i) => excludedIds.indexOf(id) !== i);
        if (duplicateExcluded.length > 0) {
            return `Duplicate entityId in excludedEntities: ${[...new Set(duplicateExcluded)].join(", ")}`;
        }
        const overlap = includedIds.filter((id) => excludedIds.includes(id));
        if (overlap.length > 0) {
            return `entityId cannot appear in both includedEntities and excludedEntities: ${[...new Set(overlap)].join(", ")}`;
        }
        const variantIds = new Set((experiment.variants || []).map((v) => v.id).filter(Boolean));
        const invalidRefs = included
            .filter((e) => e.preallocatedVariant && !variantIds.has(e.preallocatedVariant))
            .map((e) => e.preallocatedVariant);
        if (invalidRefs.length > 0) {
            return `preallocatedVariant references unknown variant id: ${[...new Set(invalidRefs)].join(", ")}`;
        }
        return undefined;
    },
    layers: (_experiment, value) => {
        if (!Array.isArray(value) || value.length === 0) {
            return "At least one layer is required";
        }
        return undefined;
    },
    variants: (_experiment, value) => {
        if (!Array.isArray(value) || value.length === 0) {
            return "At least one variant is required";
        }
        const totalWeight = value.reduce((sum, v) => sum + (v.weight || 0), 0);
        if (totalWeight !== 100) {
            return `Variant weights must sum to 100, got ${totalWeight}`;
        }
        const controlCount = value.filter((v) => v.isControl).length;
        if (controlCount !== 1) {
            return `Exactly one control variant is required, found ${controlCount}`;
        }
        const variantNames = value.map((v) => v.name);
        const duplicateNames = variantNames.filter((name, i) => variantNames.indexOf(name) !== i);
        if (duplicateNames.length > 0) {
            return `Duplicate variant names: ${[...new Set(duplicateNames)].join(", ")}`;
        }
        return undefined;
    }
};
function validateOptionalCap(field, value) {
    if (value == null) {
        return undefined; // unset = no cap
    }
    if (!Number.isSafeInteger(value) || value <= 0) {
        return `${field} must be a whole number greater than 0 when set`;
    }
    return undefined;
}
//# sourceMappingURL=validators.js.map